import { Skeleton } from "@mui/lab";
import { Card, CardContent, Grid } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { RequestNoContentReport } from "components/CallServer/RequestNoContentReport";
import MuiContainer from "components/MuiExtras/MuiContainer";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import WidgetDrawer from "components/WidgetDrawer";
import { deepMerge } from "lib/DeepMerge";
import { FeedItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import ReactResizeDetector from "react-resize-detector";
import { RouteComponentProps, withRouter } from 'react-router';
import { RoutePaths } from "routes/RouteConfig";
import { SidebarPostWindowMessages } from "store/sidebar/windowMessages";
import WidgetListResume from "views/Publication/Widgets/WidgetListResume";
import WidgetRelations from "views/Publication/Widgets/WidgetRelations";
import ListCardHeader from "views/Timeline/Components/CardHeader/ListCardHeader";
import FeedListItems, { FeedListItemsComponent } from "views/Timeline/Components/FeedListItems";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    loading: boolean;
    lastError: any;
    data: FeedItemDto;
    dialog: any;
}

interface IProps {
    timestamp?: Date;
}

interface IRouteParams {
    id: string;
}

type Props = IProps & RouteComponentProps<IRouteParams> & WithStyles<typeof TimelineStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

class TimelineListComponent extends React.PureComponent<Props, IState> {
    private feedRef: FeedListItemsComponent;

    private sidebarWindowMessages: SidebarPostWindowMessages = new SidebarPostWindowMessages(window);

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            loading: true,
            lastError: null
        };
    }

    ///
    private onLoad(id: string) {

        this.setState({ loading: true, lastError: null, data: null });

        api.getFeedItemPublication(id, true)
            .then((data) => {
                this.setState({ data, loading: false });
            })
            .catch((error) => {
                this.setState({ loading: false, lastError: error });
            });
    }

    ///
    private handleUpdateList = (item: DeepPartial<FeedItemDto>) => {
        const newData = deepMerge(this.state.data, item, { arrayMerge: (target: any, source: any, options: any) => source });

        if (this.state.data.publication.title != newData.publication.title) {
            this.sidebarWindowMessages.reset();
        }

        this.setState({ data: newData });
    };

    ///
    private handleDeleteList = (): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            api.deletePublication(this.state.data.publicationId, this.state.data.publicationType)
                .then((result) => {
                    if (result) {
                        this.sidebarWindowMessages.reset();
                        this.props.snackbar.showMessage(this.props.t("Views.Timeline.TimelineList.DeleteSuccess"), "success");
                        this.props.history.replace(RoutePaths.TimelineStartup);
                    }
                    resolve(result);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    ///
    private handleInfo = () => {
        this.setState({
            dialog: (
                <WidgetDrawer onClose={() => this.setState({ dialog: null })} >
                    <WidgetListResume data={this.state.data} />
                </WidgetDrawer>
            )
        });
    };

    ///
    private handleReload = () => {
        return api.getFeedItemPublication(this.state.data.publicationId, false)
            .then((newData) => {
                this.setState({ data: newData });
            })
            .catch(() => {
            });
    };

    ///
    private renderSkeleton() {
        return (
            <div className={this.props.classes.timelineContainer}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} >
                            <Grid item>
                                <Skeleton animation="wave" variant="rectangular" width={64} height={64} />
                            </Grid>

                            <Grid item xs>
                                <MuiContainer margin="dense">
                                    <Grid container justifyContent="space-between">
                                        <Grid item xs>
                                            <Skeleton animation="wave" height={24} width="100%" style={{ marginBottom: 6 }} />

                                            <MuiContainer margin="dense">
                                                <Skeleton animation="wave" height={22} width="50%" style={{ marginBottom: 6 }} />
                                            </MuiContainer>

                                        </Grid>
                                    </Grid>
                                </MuiContainer>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <LoadingIndicator />
            </div>
        );
    }

    ///
    public componentDidMount() {
        this.onLoad(this.props.match.params.id);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.match.url != this.props.match.url || (prevProps.timestamp != this.props.timestamp && !this.state.loading)) {
            this.onLoad(this.props.match.params.id);
        }
    }

    ///
    public render() {
        const { classes } = this.props;

        const { data, lastError, loading } = this.state;

        if (lastError != null) {
            return <RequestErrorReport error={lastError} />;
        }

        if (loading) {
            return this.renderSkeleton();
        }

        if (!loading && data == null) {
            return <RequestNoContentReport message={this.props.t("Views.Timeline.TimelineList.NotFound")} />;
        }

        return (
            <>
                <ReactResizeDetector handleWidth refreshMode="debounce" refreshRate={0}>
                    {
                        (d: ReactResizeDetectorDimensions) =>

                            <Grid container spacing={1} direction="row" style={{ flexWrap: "nowrap" }}>
                                <Grid item style={{ flexGrow: 1 }}>

                                    <div className={classes.timelineContainer}>

                                        <ListCardHeader
                                            item={data}
                                            classes={classes}
                                            showRelationsButton={d.width < 920}
                                            onInfoRequest={d.width < 920 ? this.handleInfo : null}
                                            onUpdateCard={this.handleUpdateList}
                                            onRemovePublication={this.handleDeleteList}
                                            onReloadPublication={this.handleReload}
                                            onFeedResetRequest={() => this.feedRef.feedReset()}
                                        />


                                        <FeedListItems
                                            classes={classes}
                                            onGetRefenrence={(ref) => this.feedRef = ref}
                                            listId={this.props.match.params.id}
                                            canAdd={true}
                                            canAddSuggestion={false}
                                        />

                                    </div>
                                </Grid>

                                {
                                    d.width > 919 && (data.publication.hasRelations || data.statistics.views > 0) &&
                                    <Grid item style={{ width: 350 }}>
                                        {
                                            data.publication.hasRelations &&
                                            <WidgetRelations publicationId={data.publicationId} />
                                        }
                                        {
                                            data.statistics.views > 0 &&
                                            <WidgetListResume data={data} />
                                        }
                                    </Grid>
                                }
                            </Grid>
                    }
                </ReactResizeDetector>

                {this.state.dialog}
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

const Timeline = withSnackbar(withRouter(withTranslation()(withAllStyles(TimelineListComponent))));

///////////////////////////////////////////////////////////////////////////

const TimelineList: React.FC<Props> = () => {
    return (
        <Timeline
            timestamp={new Date()}
        />
    );
};

export default TimelineList;
